import React from 'react';
import type { CSSProperties } from 'react';
import type { CvHandoffMode } from '@tabbio/types';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import type { CvHandoffSendLabels } from './CvHandoffSendSheet';

export function CvHandoffSendSheet({
  visible,
  onClose,
  initialEmail,
  initialName,
  initialMode = 'copy',
  isSending,
  hasBeenSent,
  labels,
  onSubmit,
}: {
  visible: boolean;
  onClose: () => void;
  initialEmail?: string | null;
  initialName?: string | null;
  initialMode?: CvHandoffMode;
  isSending: boolean;
  hasBeenSent?: boolean;
  labels: CvHandoffSendLabels;
  onSubmit: (input: {
    recipientEmail: string;
    recipientName?: string | null;
    mode: CvHandoffMode;
  }) => void;
}) {
  const [recipientEmail, setRecipientEmail] = React.useState(initialEmail ?? '');
  const [recipientName, setRecipientName] = React.useState(initialName ?? '');
  const [mode, setMode] = React.useState<CvHandoffMode>(initialMode);

  React.useEffect(() => {
    if (!visible) return;
    setRecipientEmail(initialEmail ?? '');
    setRecipientName(initialName ?? '');
    setMode(initialMode);
  }, [initialEmail, initialMode, initialName, visible]);

  const canSubmit = recipientEmail.trim().length > 0 && !isSending;

  return (
    <WebDialog
      visible={visible}
      title={labels.title}
      subtitle={labels.subtitle}
      onClose={onClose}
      maxWidth={520}
      footer={
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              recipientEmail: recipientEmail.trim(),
              recipientName: recipientName.trim() || null,
              mode,
            })
          }
          style={{
            ...primaryButtonStyle,
            opacity: canSubmit ? 1 : 0.4,
            cursor: canSubmit ? 'pointer' : 'default',
          }}
        >
          {isSending ? (
            <span aria-hidden style={spinnerStyle} />
          ) : (
            <MaterialSymbol name="send" size={18} />
          )}
          {hasBeenSent ? labels.resend : labels.send}
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <label style={labelStyle}>{labels.recipientName}</label>
          <input
            value={recipientName}
            onChange={(event) => setRecipientName(event.target.value)}
            placeholder={labels.recipientNamePlaceholder}
            style={fieldStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>{labels.recipientEmail}</label>
          <input
            value={recipientEmail}
            onChange={(event) => setRecipientEmail(event.target.value)}
            placeholder={labels.recipientEmailPlaceholder}
            type="email"
            style={fieldStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>{labels.modeLabel}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ModeOption
              active={mode === 'copy'}
              title={labels.copy}
              description={labels.copyDescription}
              onClick={() => setMode('copy')}
            />
            <ModeOption
              active={mode === 'transfer'}
              title={labels.transfer}
              description={labels.transferDescription}
              onClick={() => setMode('transfer')}
            />
          </div>
        </div>
      </div>
      <style>{`@keyframes cvHandoffSpin { to { transform: rotate(360deg); } }`}</style>
    </WebDialog>
  );
}

function ModeOption({
  active,
  title,
  description,
  onClick,
}: {
  active: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 12,
        width: '100%',
        padding: '14px 16px',
        borderRadius: 18,
        border: '1px solid var(--input-border)',
        background: active ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        color: 'var(--text-primary)',
        textAlign: 'start',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 18,
          height: 18,
          marginTop: 2,
          borderRadius: '50%',
          border: `2px solid ${active ? 'var(--text-primary)' : 'var(--input-border)'}`,
          background: active ? 'var(--text-primary)' : 'transparent',
          flexShrink: 0,
        }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 13, lineHeight: '18px', color: 'var(--text-secondary)' }}>
          {description}
        </span>
      </span>
    </button>
  );
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 8,
};

const fieldStyle: CSSProperties = {
  width: '100%',
  height: 52,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  padding: '0 22px',
  fontSize: 15,
  color: 'var(--text-primary)',
  background: 'var(--input-bg)',
  boxShadow: 'var(--gem-input-shadow)',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryButtonStyle: CSSProperties = {
  width: '100%',
  height: 48,
  borderRadius: 9999,
  border: 0,
  backgroundColor: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 15,
  fontWeight: 500,
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

const spinnerStyle: CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '2px solid color-mix(in srgb, var(--bg) 30%, transparent)',
  borderTopColor: 'var(--bg)',
  animation: 'cvHandoffSpin 0.8s linear infinite',
};
